import React from 'react';
import { useTranslation } from 'react-i18next';
import PhoneVerification from '../PhoneVerification';

export interface PersonalInfoSectionProps {
  name: string;
  nickname: string;
  phoneNumber: string;
  phoneVerified: boolean;
  onChange: (field: string, value: string | boolean) => void;
  errors: Record<string, string>;
  isPhoneExisting?: boolean;
  hidePhoneVerification?: boolean;
}

const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({
  name,
  nickname,
  phoneNumber,
  phoneVerified,
  onChange,
  errors,
  isPhoneExisting = false,
  hidePhoneVerification = false,
}) => {
  const { t } = useTranslation('profile');

  return (
    <div className="pt-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-2">
        {t('myProfile.form.personalInfo')}
      </h2>
      <hr className="border-gray-200 mb-4" />
      <div className="space-y-5">

      {/* Name (required) */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          {t('myProfile.form.name')} <span className="text-red-500">*</span>
        </label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={(e) => onChange('name', e.target.value)}
          placeholder={t('myProfile.form.namePlaceholder')}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:outline-none ${
            errors.name
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
          }`}
        />
        {errors.name && (
          <p className="mt-1 text-xs text-red-500">{errors.name}</p>
        )}
      </div>

      {/* Nickname (optional) */}
      <div>
        <label htmlFor="nickname" className="block text-sm font-medium text-gray-700 mb-1">
          {t('myProfile.form.nickname')}
        </label>
        <input
          id="nickname"
          type="text"
          value={nickname}
          onChange={(e) => onChange('nickname', e.target.value)}
          placeholder={t('myProfile.form.nicknamePlaceholder')}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Phone Number + Phone Verification */}
      <div>
        <label htmlFor="phoneNumber" className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
          {t('myProfile.form.phoneNumber')}
          <span className="relative group">
            <span className="cursor-help text-gray-400 hover:text-gray-600">ℹ️</span>
            <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-64 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg z-10">
              {t('myProfile.form.phoneTooltip')}
            </span>
          </span>
        </label>
        <PhoneVerification
          phoneNumber={phoneNumber}
          isVerified={phoneVerified}
          onVerified={() => onChange('phoneVerified', true)}
          onPhoneChange={(phone) => onChange('phoneNumber', phone)}
          isExisting={isPhoneExisting}
          hideVerifyButton={hidePhoneVerification}
        />
        {errors.phoneNumber && (
          <p className="mt-1 text-xs text-red-500">{errors.phoneNumber}</p>
        )}
      </div>
      </div>
    </div>
  );
};

export default PersonalInfoSection;
